import {AbstractHttpRequest, MapHttpHeaders} from '../../http/exchange.js';
import type {
    BodyChunk,
    HttpMethod, HttpResponse,
    MutableHttpHeaders,
    ReadonlyHeaderValue,
    ReadonlyHttpHeaders
} from '../../../types/web/http';
import {ServerHttpRequest} from '../../../types/web/server';

import stream from 'node:stream/web';
import {AbstractServerHttpResponse, DefaultWebExchange} from '../../server/exchange.js';

import type {Principal} from '../../../types/auth';

export class MockServerHttpRequest extends AbstractHttpRequest<ReadonlyHttpHeaders> implements ServerHttpRequest {
    readonly #url: URL;
    #body?: Blob;
    upgrade = false;

    constructor(url: URL | string, method?: HttpMethod) {
        super(new MapHttpHeaders());
        if (typeof url === 'string') {
            if (URL.canParse(url)) {
                url = new URL(url);
            } else if (URL.canParse(url, 'http://localhost')) {
                url = new URL(url, 'http://localhost');
            } else {
                throw new TypeError('URL cannot parse url');
            }
        }
        this.#url = url;
        this.method = method ?? 'GET';
        this.setHeader('Host', this.#url.hostname);
        this.path = this.#url.pathname ?? '/';
    }

    method: HttpMethod;
    path: string;

    get host() {
        return super.parseHost(this.#url.host);
    }

    get protocol() {
        return super.parseProtocol((this.#url.protocol).slice(0, -1));
    }

    get body() {
        if (this.#body !== undefined) {
            const blob = this.#body;
            const asyncIterator = (async function* () {
                const bytes = await blob.bytes();
                yield bytes;
            })();
            return stream.ReadableStream.from(asyncIterator) as ReadableStream<Uint8Array>;
        }
    }

    blob(): Promise<Blob> {
        const body = this.#body;
        return body ? Promise.resolve(body) : Promise.reject(new Error(`no body set`));
    }
    async text() {
        const blob = await this.blob();
        return await blob.text();
    }

    async formData() {
        const blob = await this.blob();
        const text = await blob.text();
        return new URLSearchParams(text);
    }

    async json() {
        const blob = await this.blob();
        if (blob.size === 0) {
            return undefined;
        }
        const text = await blob.text();
        return JSON.parse(text);
    }

    async writeBody(body?: ReadableStream<BodyChunk> | Promise<BodyChunk> | BodyChunk) {
        if (body === undefined) {
            this.#body = new Blob([]);
            return;
        }
        if (body instanceof ReadableStream) {
            const chunks: BodyChunk[] = [];
            const reader = body.getReader();
            let done = false;
            while (!done) {
                const {value, done: readDone} = await reader.read();
                if (readDone) {
                    done = true;
                } else {
                    chunks.push(value);
                }
            }
            this.#body = new Blob(chunks);
        }
        else {
            body = await body;
            if (typeof body === 'string') {
                this.#body = new Blob([body], {type: 'text/plain'});
            } else {
                this.#body = new Blob([body]);
            }
        }
        if (!this.headers.has('content-type')) {
            this.setHeader('Content-Type', this.#body.type || 'application/octet-stream');
        }

    }

    get URL() {
        return new URL(this.path + this.#url.search + this.#url.hash, `${this.protocol}://${this.host}`);
    }

    addHeader(name: string, value: string | (readonly string[])): this {
        (this.headers as MutableHttpHeaders).add(name, value);
        return this;
    }

    setHeader(name: string, value: ReadonlyHeaderValue): this {
        (this.headers as MutableHttpHeaders).set(name, value);
        return this;
    }
}

export class MockServerHttpResponse extends AbstractServerHttpResponse implements HttpResponse<MutableHttpHeaders> {
    #writeHandler: (body: ReadableStream<Uint8Array>) => Promise<boolean>;
    #body: () => ReadableStream<Uint8Array> = () => {
        throw new Error('No content was written to the response body nor end was called on this response.');
    };

    constructor() {
        super(new MapHttpHeaders());
        this.#writeHandler = async (body) => {
            const chunks: Uint8Array[] = [];
            let bodyStream: ReadableStream<Uint8Array> | undefined;
            this.#body = () => {
                bodyStream ??= stream.ReadableStream.from(chunks) as ReadableStream<Uint8Array>;
                return bodyStream;
            };

            const reader = body.getReader();
            let done = false;
            do {
                const {value, done: readDone} = await reader.read();
                if (readDone) {
                    done = true;
                } else {
                    chunks.push(value);
                }
            } while (!done);
            return true;
        }
    }

    get statusCode() {
        return super.statusCode!; // :(
    }

    set writeHandler(handler: (body: ReadableStream<Uint8Array>) => Promise<boolean>) {
        this.#body = () => {
            throw new Error('Not available with custom write handler');
        }
        this.#writeHandler = handler;
    }


    getNativeResponse<T>(): T {
        throw new Error('This is a mock. No running server, no native response.');
    }

    protected applyStatusCode(): void {}
    protected applyHeaders(): void {}
    protected applyCookies(): void {
        for (const cookie of this.cookies) {
            this.headers.add('Set-Cookie', super.setCookieValue(cookie));
        }
    }

    bodyInternal(body: Promise<BodyChunk | void>): Promise<boolean> {
        const it = (async function* () {
            const resolved = await body;
            if (resolved === undefined) {
                return;
            }
            yield resolved;
        })();
        return this.#writeHandler(stream.ReadableStream.from(it) as ReadableStream<Uint8Array>);
    }

    async end(): Promise<boolean> {
        return this.doCommit(async () => {
            return await new Promise((resolve, reject) => {
                this.#writeHandler(stream.ReadableStream.from([]) as ReadableStream);
            });
        });
    }

    getBody() {
        return this.#body();
    }
}


export class MockWebExchange extends DefaultWebExchange<MockServerHttpRequest, MockServerHttpResponse> {
    readonly #principal?: Principal;
    constructor(request: MockServerHttpRequest, response?: MockServerHttpResponse, principal?: Principal) {
        super(request, response ?? new MockServerHttpResponse());
        this.#principal = principal;
    }

    principal<P extends Principal>(): Promise<P | undefined> {
        return Promise.resolve(this.#principal as P);
    }
}
